class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
    this.renderSuggestions(parsedState);
  }

  // A small version of a card image: the srcset entry closest to 165px wide, else its src.
  static smallImage(image) {
    const srcset = image.getAttribute('srcset') || '';
    const candidates = srcset
      .split(',')
      .map((entry) => entry.trim().split(/\s+/))
      .filter((parts) => parts[0] && /^\d+w$/.test(parts[1] || ''))
      .map((parts) => ({ url: parts[0], width: parseInt(parts[1], 10) }));
    if (candidates.length) {
      candidates.sort((a, b) => Math.abs(a.width - 165) - Math.abs(b.width - 165));
      return candidates[0].url;
    }
    return image.currentSrc || image.getAttribute('src');
  }

  // "Goes with" suggestions for the item just added: the first row of the product page's Goes with
  // section (sections/restore-cross-sell.liquid), which is already limited to the same store. On
  // the product page it is read from the page; from a collection card it is fetched from the
  // product page. Shows up to three items and stays hidden when there is nothing to suggest.
  renderSuggestions(parsedState) {
    const box = document.getElementById('cart-notification-suggestions');
    if (!box) return;
    box.hidden = true;
    box.replaceChildren();
    const productId = parsedState && parsedState.product_id;
    const url = parsedState && parsedState.url;
    if (!productId || !url) return;
    const request = (this.suggestionRequest = Symbol('suggestions'));

    const fromDocument = (doc) => doc.querySelector('.restore-cross-sell__row');
    const pageProduct = document.querySelector('product-info[data-product-id]');
    const onThisProduct = pageProduct && String(pageProduct.dataset.productId) === String(productId);
    const rowPromise = onThisProduct
      ? Promise.resolve(fromDocument(document))
      : fetch(url.split('?')[0])
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => (html ? fromDocument(new DOMParser().parseFromString(html, 'text/html')) : null))
          .catch(() => null);

    rowPromise.then((row) => {
      if (!row || request !== this.suggestionRequest) return;
      const items = [...row.querySelectorAll('.grid__item')].slice(0, 3).map((li) => {
        const link = li.querySelector('.card__heading a');
        const image = li.querySelector('.card__media img');
        const price = li.querySelector('.price-item--last') || li.querySelector('.price-item');
        return link && {
          href: link.getAttribute('href'),
          title: link.textContent.trim(),
          image: image && CartNotification.smallImage(image),
          price: price ? price.textContent.trim() : '',
        };
      }).filter(Boolean);
      if (!items.length) return;

      const heading = document.createElement('p');
      heading.className = 'cart-notification-suggestions__heading';
      heading.textContent = (row.querySelector('.restore-cross-sell__heading') || {}).textContent || 'Goes with this';
      const list = document.createElement('ul');
      list.className = 'cart-notification-suggestions__list list-unstyled';
      items.forEach((item) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'cart-notification-suggestions__item';
        a.href = item.href;
        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.width = 64;
          img.height = 64;
          img.loading = 'eager';
          a.appendChild(img);
        }
        const text = document.createElement('span');
        text.className = 'cart-notification-suggestions__text';
        const title = document.createElement('span');
        title.className = 'cart-notification-suggestions__title';
        title.textContent = item.title;
        const price = document.createElement('span');
        price.className = 'cart-notification-suggestions__price';
        price.textContent = item.price;
        text.append(title, price);
        a.appendChild(text);
        li.appendChild(a);
        list.appendChild(li);
      });
      box.append(heading, list);
      box.hidden = false;
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
